import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { TEAM, SITE } from "../data/site";

export default function TeamPage() {
    // Core team = 5 principals (founders + senior leadership).
    // Extended team = the specialist trades (10 people).
    const core = TEAM.slice(0, 5);
    const extended = TEAM.slice(5);

    return (
        <div className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A] font-sans">
            <header className="border-b border-[#1A1A1A]/10 bg-[#FAF9F6]/95 backdrop-blur-md sticky top-0 z-30">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between py-5">
                    <Link
                        to="/"
                        data-testid="team-back-home"
                        className="overline text-[#4A4643] hover:text-[#1A1A1A] transition-colors inline-flex items-center gap-2"
                    >
                        <ArrowLeft size={14} strokeWidth={1.6} />
                        Back home
                    </Link>
                    <div className="overline text-[#8C7C6D]">The Team</div>
                    <Link
                        to="/#contact"
                        data-testid="team-contact-link"
                        className="overline text-[#1A1A1A] dd-link"
                    >
                        Contact
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="pt-16 pb-14 lg:pt-24 lg:pb-20">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-6 lg:gap-12 items-end">
                    <div className="col-span-12 lg:col-span-7">
                        <div className="overline text-[#4A4643] mb-6">
                            People
                        </div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight text-[#1A1A1A]">
                            The <span className="italic text-[#8C7C6D]">team</span>
                            <br />
                            behind the practice.
                        </h1>
                    </div>
                    <div className="col-span-12 lg:col-span-4 lg:col-start-9">
                        <p className="text-[#4A4643] text-base lg:text-lg leading-relaxed font-light">
                            Design Desire is a small studio backed by a wider circle of
                            specialists — designers, consultants and craftspeople who
                            we&apos;ve worked with for years.
                        </p>
                    </div>
                </div>
            </section>

            {/* Core team — premium editorial cards on a warm cream section */}
            <section
                className="py-16 lg:py-24 bg-[#F0EFEA] border-y border-[#1A1A1A]/10"
                data-testid="core-team-section"
            >
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="flex items-end justify-between mb-10 lg:mb-14 flex-wrap gap-4">
                        <div>
                            <div className="overline text-[#8C7C6D] mb-4">
                                    Core team
                            </div>
                            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A1A]">
                                Principals & leadership.
                            </h2>
                        </div>
                        <div className="overline text-[#4A4643]">
                            {String(core.length).padStart(2, "0")} members
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 lg:gap-8">
                        {core.map((m, i) => (
                            <CoreCard key={m.name} member={m} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Extended team */}
            <section
                className="py-16 lg:py-24"
                data-testid="extended-team-section"
            >
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="flex items-end justify-between mb-10 lg:mb-14 flex-wrap gap-4">
                        <div>
                            <div className="overline text-[#4A4643] mb-4">
                                    Studio &amp; Specialists
                            </div>
                            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A1A]">
                                The wider circle.
                            </h2>
                        </div>
                        <div className="overline text-[#4A4643]">
                            {String(extended.length).padStart(2, "0")} specialists
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 lg:gap-8">
                        {extended.map((m, i) => (
                            <SpecialistCard key={m.name} member={m} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#1A1A1A] text-[#FAF9F6] py-20">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 text-center">
                    <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
                        Want to work with us?
                    </h2>
                    <p className="mt-4 text-[#FAF9F6]/70 max-w-xl mx-auto font-light">
                        Reach out at{" "}
                        <a
                            href={`mailto:${SITE.email}`}
                            className="dd-link text-[#C7B299]"
                        >
                            {SITE.email}
                        </a>{" "}
                        or send us a note.
                    </p>
                    <Link
                        to="/#contact"
                        data-testid="team-cta-contact"
                        className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-[#FAF9F6] text-[#1A1A1A] text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#C7B299] transition-colors"
                    >
                        Start a conversation
                        <ArrowUpRight size={16} strokeWidth={1.5} />
                    </Link>
                </div>
            </section>
        </div>
    );
}

/**
 * Core team card — premium editorial styling on the warm-cream section.
 * Image is full-bleed inside a portrait frame with a subtle ring to
 * separate it from the extended team.
 */
const CoreCard = ({ member, index }) => (
    <motion.article
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, delay: index * 0.06 }}
        data-testid={`core-member-${index}`}
    >
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#E5E1D8] ring-1 ring-[#1A1A1A]/15">
            <img
                src={member.image}
                alt={member.name}
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="lazy"
            />
        </div>
        <div className="mt-5">
            <h3 className="font-serif text-xl lg:text-2xl text-[#1A1A1A] tracking-tight leading-tight">
                {member.name}
            </h3>
            <p className="overline text-[#8C7C6D] mt-2">{member.role}</p>
            <p className="text-[13px] text-[#4A4643] mt-3 leading-relaxed font-light">
                {member.bio}
            </p>
        </div>
    </motion.article>
);

/**
 * Specialist card — smaller, denser grid without ring so the visual weight
 * naturally reads secondary to the core team.
 */
const SpecialistCard = ({ member, index }) => (
    <motion.article
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        data-testid={`specialist-member-${index}`}
    >
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#F0EFEA]">
            <img
                src={member.image}
                alt={member.name}
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="lazy"
            />
        </div>
        <h4 className="font-serif text-base lg:text-lg mt-4 text-[#1A1A1A] tracking-tight leading-snug">
            {member.name}
        </h4>
        <p className="overline text-[#8C7C6D] mt-1 text-[10px]">
            {member.role}
        </p>
    </motion.article>
);
